import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { SAS_COUNT } from '@/crypto';
import { SAS_EMOJI, type Sas } from '@/lib/nearby';
import { useLatest } from '@/lib/useLatest';

import { EmojiRow } from './Nearby';
import styles from './nearby.module.css';

/** Emoji in one spinning strip (the strip loops: its last tile repeats the first). */
const SPIN_LENGTH = 12;
/** One turn of each reel, ms: a little different, so they never spin in step. */
const SPIN_MS: readonly number[] = Array.from({ length: SAS_COUNT }, (_, index) => 780 + index * 140);
/** Emoji that run past before a reel stops on its own. */
const RUN_LENGTH = 9;
/** How long a reel takes to stop, and how much later than the one before it starts stopping, ms. */
const LAND_MS = 720;
const LAND_STAGGER = 260;
/** Slows down to a stop and settles back from a hair past it, as a real reel does. */
const LAND_EASE = 'cubic-bezier(0.18, 0.8, 0.26, 1.06)';
const ALL_LANDED = (1 << SAS_COUNT) - 1;

function reducedMotion(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function canSpin(): boolean {
  return typeof Element !== 'undefined' && typeof Element.prototype.animate === 'function' && !reducedMotion();
}

function randomEmoji(count: number): Sas[] {
  return Array.from({ length: count }, () => SAS_EMOJI[Math.floor(Math.random() * SAS_EMOJI.length)] ?? SAS_EMOJI[0]).filter((e): e is Sas => e !== undefined);
}

/**
 * The four emoji to compare, as a row of slot-machine reels: while the other
 * device's key is on its way they spin through the emoji table, so it is
 * plain something is coming; once the four are known the reels stop on them
 * one after another. Then the plain row takes their place (`EmojiRow`, with
 * the names for screen readers and `testId`). With reduced motion, or where
 * the browser cannot animate, there is no spin: four quiet tiles, then the row.
 */
export function EmojiReels({ emoji, testId }: { readonly emoji: readonly Sas[] | null; readonly testId?: string }): React.JSX.Element {
  const [spin] = useState(canSpin);
  const [landed, setLanded] = useState(0);
  if (emoji && (!spin || landed === ALL_LANDED)) return <EmojiRow emoji={emoji} testId={testId} />;
  return (
    <p className={styles.emoji} aria-hidden="true" data-testid="nearby-reels">
      {Array.from({ length: SAS_COUNT }, (_, index) => index).map((index) => (
        <Reel
          key={index}
          index={index}
          target={emoji?.[index] ?? null}
          spin={spin}
          onLanded={() => {
            setLanded((done) => done | (1 << index));
          }}
        />
      ))}
    </p>
  );
}

function Reel({ index, target, spin, onLanded }: { readonly index: number; readonly target: Sas | null; readonly spin: boolean; readonly onLanded: () => void }): React.JSX.Element {
  const strip = useRef<HTMLSpanElement>(null);
  const [loop] = useState(() => {
    const items = randomEmoji(SPIN_LENGTH);
    return [...items, ...items.slice(0, 1)];
  });
  const [run, setRun] = useState<Sas[] | null>(null);
  const landed = useLatest(onLanded);

  // the emoji are known: this reel starts to stop, a beat after the one before it
  useEffect(() => {
    if (!spin || target === null) return undefined;
    const timer = window.setTimeout(() => {
      setRun([...randomEmoji(RUN_LENGTH), target]);
    }, index * LAND_STAGGER);
    return () => {
      window.clearTimeout(timer);
    };
  }, [spin, target, index]);

  // run the last stretch of the strip down to the target and stop on it
  useLayoutEffect(() => {
    const el = strip.current;
    if (run === null || !el) return undefined;
    const tile = el.firstElementChild?.getBoundingClientRect().height ?? 0;
    const end = `translateY(${String(-RUN_LENGTH * tile)}px)`;
    const animation = el.animate([{ transform: 'translateY(0)' }, { transform: end }], { duration: LAND_MS, easing: LAND_EASE, fill: 'forwards' });
    animation.onfinish = () => {
      el.style.transform = end;
      landed.current();
    };
    return () => {
      animation.onfinish = null;
      animation.cancel();
    };
    // the reel lands once per run; a new parent callback does not restart it
  }, [run, landed]);

  if (!spin) return <span className={styles.reel} />;
  const items = run ?? loop;
  return (
    <span className={styles.reel}>
      <span className={styles.reelWindow}>
        <span
          ref={strip}
          className={run ? styles.reelStrip : [styles.reelStrip, styles.reelSpin].join(' ')}
          style={run ? undefined : ({ '--spin': `${String(SPIN_MS[index] ?? 900)}ms`, '--shift': `${String((-100 * (items.length - 1)) / items.length)}%` } as React.CSSProperties)}
        >
          {items.map((item, at) => (
            <span key={`${String(at)}-${item.name}`} className={styles.reelItem}>
              {item.emoji}
            </span>
          ))}
        </span>
      </span>
    </span>
  );
}
